import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
});

let accessToken = null;
export function setAccessToken(t) { accessToken = t || null; }
export function getAccessToken() { return accessToken; }

// Attach token
api.interceptors.request.use((config) => {
  const t = getAccessToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// --- 401 refresh logic (skip auth endpoints) ---
const AUTH_SKIP = new Set([
  "/api/auth/login",
  "/api/auth/register",
  "/api/auth/verify-email",
  "/api/auth/forgot-password",
  "/api/auth/reset-password",
  "/api/auth/refresh",
]);

let isRefreshing = false;
let waiters = [];

function onRefreshed(newToken) {
  waiters.forEach(({ resolve }) => resolve(newToken));
  waiters = [];
}
function waitForRefresh() {
  return new Promise((resolve) => waiters.push({ resolve }));
}
function getPath(url) {
  try {
    // handles both absolute and relative URLs
    const u = new URL(url, api.defaults.baseURL || window.location.origin);
    return u.pathname;
  } catch {
    return url; // fallback
  }
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const { response, config } = err || {};
    const status = response?.status;
    const path = getPath(config?.url || "");

    // If this is an auth endpoint or not a 401 -> just fail fast
    if (status !== 401 || AUTH_SKIP.has(path)) {
      throw err;
    }

    // Prevent infinite loops
    if (config._retry) throw err;
    config._retry = true;

    try {
      if (isRefreshing) {
        const t = await waitForRefresh();
        if (t) config.headers.Authorization = `Bearer ${t}`;
        return api(config);
      }

      isRefreshing = true;
      const { data } = await api.post("/api/auth/refresh"); // cookie carries refresh
      if (!data?.accessToken) throw new Error("No token on refresh");
      setAccessToken(data.accessToken);
      onRefreshed(data.accessToken);
      config.headers.Authorization = `Bearer ${data.accessToken}`;
      return api(config);
    } catch (e) {
      waiters = [];
      setAccessToken(null);
      throw err; // surface the original 401 to the caller (your page can show error)
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
